import { PortableText } from "@portabletext/react";
import { useNavigate } from "@tanstack/react-router";
import React from "react";
import { useTranslation } from "react-i18next";

import useOpportunitiesQuery from "../api/sanity/queries/getOpportunities";
import useSyncsQuery from "../api/sanity/queries/getSyncs";
import useGetColours from "../helpers/getColours";
import useAppStore from "../store/appStateZustand";
import LoadingSmall from "./LoadingSmall";
import RebrandPrimaryButton from "./RebrandPrimaryButton";

function DashboardOpportunitiesBlock() {
  const { data: syncsData, isFetching, isSuccess } = useSyncsQuery(); // sanity
  const { data: oppsData } = useOpportunitiesQuery(); // sanity
  const { t } = useTranslation("dashboard");
  const { highlightColour } = useGetColours();
  const navigate = useNavigate();

  // Use reactive selectors instead of getState() for proper re-renders
  const allSyncsAvailable = useAppStore(state => state.allSyncsAvailable);
  const allOpportunitiesAvailable = useAppStore(state => state.allOpportunitiesAvailable);

  let syncs = [];
  let opps = [];
  // Conditionally set syncs based on allSyncsAvailable
  if (allSyncsAvailable) {
    syncs = syncsData;
  }
  else {
    syncs = syncsData?.syncs_available;
  }

  // Conditionally set opps based on allOpportunitiesAvailable
  if (allOpportunitiesAvailable) {
    opps = oppsData;
  }
  else {
    opps = oppsData?.opportunities_available;
  }

  return (
    <>
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3">
        {isFetching && <LoadingSmall />}
        {isSuccess ? (
          opps || syncs ? (
            [...(opps || []).slice(0, 2), ...(syncs || []).slice(0, 4)].map(
              item => (
                <React.Fragment key={item._id}>
                  <div className="flex flex-col w-full col-span-1 lg:mb-0">
                    <div className="relative">
                      <img
                        src={item.imageUrl}
                        alt={item.altText}
                        className="object-cover object-center w-full rounded-t-lg rounded-b-lg min-h-32 md:min-h-70 max-h-48"
                      />
                      <span
                        style={{ backgroundColor: highlightColour }}
                        className="absolute top-0 left-0 px-2 py-1 text-sm text-white rounded-tl-lg rounded-br-lg w-max post-type-tag bg-opacity-80"
                      >
                        {item._type === "syncs" ? (
                          <p>{t("dashboard:sync-tag", "Sync")}</p>
                        ) : item._type === "opportunities" ? (
                          <p>{t("dashboard:news-tag", "News")}</p>
                        ) : null}
                      </span>
                    </div>
                    <div
                      className="flex flex-col justify-between flex-auto gap-2 p-8 text-left rounded-b-lg"
                      style={{ backgroundColor: "#ffffff" }}
                    >
                      <h2 className="grow line-clamp-2">{item.name}</h2>
                      <div
                        style={{ color: "#868484" }}
                        className="text-sm font-medium font-sentric-alt line-clamp-3 dashboard-html-cards"
                      >
                        <PortableText value={item.description} />
                      </div>
                      <div className="flex justify-start w-full mt-2">
                        <RebrandPrimaryButton
                          onClick={() => {
                            if (item._type === "opportunities") {
                              navigate({ to: "/opportunities" });
                            }
                            else {
                              navigate({ to: "/syncs" });
                            }
                          }}
                          arrow
                        >
                          {t("dashboard:opportunities-find-out-more", "Find out more")}
                        </RebrandPrimaryButton>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              ),
            )
          ) : (
            <div>
              <p>{t("other:no-opportunities", "No opportunities available")}</p>
            </div>
          )
        ) : null}
      </div>
    </>
  );
}

export default DashboardOpportunitiesBlock;
